import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { FeedbackDetails, FeedbackItem, FeedbackItemType } from '../../../../types/course';
import DashedPanel from './shared/DashedPanel';
import ReminderCallout from './shared/ReminderCallout';
import { FEEDBACK_REGISTRY, getFeedbackEntry } from './itemRegistry';
import { feedbackDetailsSchema } from './validation';

interface FeedbackEditorProps {
  module: {
    id: string;
    title: string;
    type: string;
    details: Partial<FeedbackDetails> & Record<string, unknown>;
  };
  sectionId: string;
  courseId: string;
  onUpdate: (moduleId: string, updates: Record<string, unknown>) => Promise<void>;
  onDelete: (moduleId: string) => Promise<void>;
}

function normalizeDetails(raw: Partial<FeedbackDetails> | undefined): FeedbackDetails {
  return {
    items: Array.isArray(raw?.items) ? (raw!.items as FeedbackItem[]) : [],
    anonymous: !!raw?.anonymous,
  };
}

export default function FeedbackEditor({ module, onUpdate }: FeedbackEditorProps) {
  const [details, setDetails] = useState<FeedbackDetails>(() => normalizeDetails(module.details));
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSaveRef = useRef<FeedbackDetails | null>(null);
  const lastModuleIdRef = useRef(module.id);
  // The parent recreates onUpdate every render; going through a ref keeps the
  // flush-on-unmount effect from re-running (and double-saving) per render.
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  useEffect(() => {
    if (module.id !== lastModuleIdRef.current) {
      lastModuleIdRef.current = module.id;
      setDetails(normalizeDetails(module.details));
      return;
    }
    // Same module, refreshed data — e.g. reopening the overlay while the
    // invalidated modules query was still refetching. Adopt the incoming
    // details only when no local edit is pending, so fresh server truth wins
    // without ever clobbering in-flight typing.
    if (saveTimeoutRef.current === null) {
      const incoming = normalizeDetails(module.details);
      setDetails((prev) =>
        JSON.stringify(prev) === JSON.stringify(incoming) ? prev : incoming,
      );
    }
  }, [module.id, module.details]);

  const persist = useCallback(
    (next: FeedbackDetails) => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      pendingSaveRef.current = next;
      saveTimeoutRef.current = setTimeout(() => {
        saveTimeoutRef.current = null; // fired — no edit pending any more
        pendingSaveRef.current = null;
        const result = feedbackDetailsSchema.safeParse(next);
        if (!result.success) {
          // Skip the autosave while the aggregate is still incomplete —
          // the next edit will re-trigger this check once the form is valid.
          return;
        }
        void onUpdateRef.current(module.id, { details: result.data });
      }, 1500);
    },
    [module.id],
  );

  // Flush (not discard) a debounced save when the editor unmounts or switches
  // modules — otherwise "toggle a setting and close the overlay within 1.5s"
  // silently loses the change. Depends on module.id ONLY: re-running per
  // render would fire duplicate PUTs that can race each other.
  useEffect(() => {
    const moduleId = module.id;
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      const pending = pendingSaveRef.current;
      pendingSaveRef.current = null;
      if (pending) {
        const result = feedbackDetailsSchema.safeParse(pending);
        if (result.success) {
          void onUpdateRef.current(moduleId, { details: result.data });
        }
      }
    };
  }, [module.id]);

  const updateDetails = useCallback(
    (patch: Partial<FeedbackDetails>) => {
      setDetails((prev) => {
        const next = { ...prev, ...patch };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const addItem = useCallback(
    (type: FeedbackItemType) => {
      setDetails((prev) => {
        const entry = getFeedbackEntry(type);
        const newItem = entry.defaultItem(prev.items.length);
        const next = { ...prev, items: [...prev.items, newItem] };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const updateItem = useCallback(
    (id: string, patch: Partial<FeedbackItem>) => {
      setDetails((prev) => {
        const next = {
          ...prev,
          items: prev.items.map((it) => (it.id === id ? ({ ...it, ...patch } as FeedbackItem) : it)),
        };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const deleteItem = useCallback(
    (id: string) => {
      setDetails((prev) => {
        if (prev.items.length <= 1) return prev;
        const remaining = prev.items.filter((it) => it.id !== id);
        const next = {
          ...prev,
          items: remaining.map((it, idx) => ({ ...it, order_index: idx })) as FeedbackItem[],
        };
        persist(next);
        return next;
      });
    },
    [persist],
  );


  const sortedItems = useMemo(
    () => [...details.items].sort((a, b) => a.order_index - b.order_index),
    [details.items],
  );

  return (
    <div className="flex flex-col gap-7 w-full max-w-[920px] mx-auto">
      <DashedPanel>
        <h2 className="mt-0 mb-6 text-center font-sans font-bold text-2xl leading-9 text-text-primary">
          Create Survey
        </h2>

        <div className="flex flex-col gap-6">
          <AnimatePresence initial={false}>
            {sortedItems.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
              >
                <FeedbackItemRow
                  item={item}
                  onChange={(patch) => updateItem(item.id, patch)}
                  onDelete={() => deleteItem(item.id)}
                />
              </motion.div>
            ))}
          </AnimatePresence>

          {sortedItems.length === 0 && (
            <p className="text-center py-6 font-sans text-[13px] text-text-secondary">
              Pick a question type below to start your survey.
            </p>
          )}
        </div>

        <div className="mt-8 pt-6 border-t border-primary-500/20">
          <p className="text-center mb-3 font-sans text-[13px] text-text-secondary">
            Add a new question
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {FEEDBACK_REGISTRY.map(({ type, label, description, Icon }) => (
              <button
                key={type}
                type="button"
                onClick={() => addItem(type)}
                className="flex items-center gap-3 text-left px-4 py-3.5 rounded-2xl border-2 border-primary-500 bg-primary-500/10 text-primary-500 transition-all hover:-translate-y-0.5 cursor-pointer"
              >
                <Icon />
                <div className="flex flex-col">
                  <span className="font-sans font-bold text-sm uppercase text-primary-500">
                    {label}
                  </span>
                  <span className="font-sans text-xs text-text-secondary">{description}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <label className="mt-6 flex items-center gap-3 self-start font-sans text-sm text-text-primary cursor-pointer">
          <input
            type="checkbox"
            checked={details.anonymous}
            onChange={(e) => updateDetails({ anonymous: e.target.checked })}
            className="w-[18px] h-[18px] accent-primary-500"
          />
          Collect responses anonymously
        </label>
      </DashedPanel>

      <ReminderCallout
        storageKey="survey-editor-reminder-dismissed"
        title="Reminder"
        message="Scaling answers let learners rate on a 3, 5 or 10-point scale. Free text answers let them share their thoughts in detail. You can preview either at any time."
      />
    </div>
  );
}

// ─── Internal row wrapper ───────────────────────────────────────────────

function FeedbackItemRow({
  item,
  onChange,
  onDelete,
}: {
  item: FeedbackItem;
  onChange: (patch: Partial<FeedbackItem>) => void;
  onDelete: () => void;
}) {
  const entry = getFeedbackEntry(item.type);
  const Component = entry.Component;
  return (
    <div className="relative px-4 py-4 rounded-2xl border border-primary-500/20 bg-surface-primary">
      <Component item={item} onChange={onChange} onDelete={onDelete} />
    </div>
  );
}
